import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/common/ui/card';
import { Bar } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip, } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { PillarData } from '../../../types/PillarData';
import { getChartTooltipConfig } from '@/common/components/charts/chartTooltipConfig';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

interface ScoresChartProps {
  pillars: PillarData[];
  teamId?: number | null;
  assessmentPeriodId?: number | null;
  teamName?: string | null;
  /** Override the chart area height (px). Defaults to min 288 / max 500. */
  chartHeight?: number;
}

// Pure helper to compute chart data from pillars and selected aggregate. Exported for testing.
export interface ComputeChartDataOptions {
  teamDataLabelMode?: 'score' | 'delta';
}

export function computeChartData(
  pillars: PillarData[],
  teamLabel?: string,
  options: ComputeChartDataOptions = { teamDataLabelMode: 'score' }
) {
  const sortedPillars = [...pillars]
    .map((pillar, index) => ({ pillar, originalIndex: index }))
    .sort((a, b) => {
      const aOrder = a.pillar.sort_order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.pillar.sort_order ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.originalIndex - b.originalIndex;
    })
    .map((item) => item.pillar);

  const teamData = sortedPillars.map((p) => p.score);
  const orgData = sortedPillars.map((p) => p.org_avg);
  const deltas = sortedPillars.map((p) => p.score - p.org_avg).map((delta) => {
    const rounded = Number(delta.toFixed(1));
    return Object.is(rounded, -0) ? 0 : rounded;
  });
  const labels = sortedPillars.map((p) => p.name);
  const datasets: any[] = [];

  const teamDataLabelMode = options.teamDataLabelMode ?? 'delta';

  // Team should be shown first
  datasets.push({
    label: teamLabel || 'Team',
    data: teamData,
    backgroundColor: 'rgba(59, 130, 246, 0.9)',
    borderColor: 'rgba(59, 130, 246, 1)',
    borderWidth: 1,
    borderRadius: 6,
    datalabels: {
      display: true,
      align: 'end',
      anchor: 'end',
      color: (context: any) => {
        const delta = deltas[context.dataIndex];
        if (delta > 0) return '#10b981';
        if (delta < 0) return '#ef4444';
        return '#2563eb';
      },
      font: {
        size: 11,
        weight: 'bold',
      },
      formatter: (value: number, context: any) => {
        if (teamDataLabelMode === 'score') {
          return value.toFixed(1);
        }
        const delta = deltas[context.dataIndex];
        const deltaStr = delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
        return deltaStr;
      },
    },
  });

  datasets.push({
    label: 'Organization',
    data: orgData,
    backgroundColor: 'rgba(156, 163, 175, 0.6)',
    borderColor: 'rgba(156, 163, 175, 0.8)',
    borderWidth: 1,
    borderRadius: 6,
    datalabels: {
      display: true,
      align: 'end',
      anchor: 'end',
      color: '#4b5563',
      font: {
        size: 11,
        weight: 600,
      },
      formatter: (value: number) => value.toFixed(1),
    },
  });


  return { labels, datasets, deltas, pillars: sortedPillars };
}

export const ScoresChart: React.FC<ScoresChartProps> = ({ pillars, teamId, assessmentPeriodId, teamName, chartHeight }) => {
  const isSinglePillar = pillars.length === 1;
  const chartTitle = `Team and Organization Scores`;
  const chartDescription = isSinglePillar
    ? "Detailed comparison for selected pillar"
    : "Visual comparison across all culture pillars";
  const effectiveDescription = chartDescription;

  const { chartData, deltas, pillars: sortedPillars } = useMemo(() => {
    const result = computeChartData(pillars, teamName ?? undefined);
    return {
      chartData: { labels: result.labels, datasets: result.datasets },
      deltas: result.deltas,
      pillars: result.pillars,
    };
  }, [pillars, teamName]);

  const chartKey = useMemo(() => {
    const dataHash = sortedPillars.map(p => `${p.name}:${p.score}:${p.org_avg}`).join('|');
    return `scores-chart-${assessmentPeriodId ?? 'no-period'}-${teamId ?? 'no-team'}-${dataHash}`;
  }, [sortedPillars, teamId, assessmentPeriodId]);

  // Fixed Y-axis maximum for score charts
  const yAxisMax = 10; // enforce 0..10 scoring range consistently

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          font: {
            size: 11,
            family: 'Poppins, sans-serif',
          },
          padding: 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        ...getChartTooltipConfig(),
        callbacks: {
          afterLabel: (context: any) => {
            const dataIndex = context.dataIndex;
            const delta = deltas[dataIndex];
            if (delta !== undefined && delta !== null && !isNaN(delta)) {
              const deltaStr = delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
              const direction = delta > 0 ? '(above org)' : delta < 0 ? '(below org)' : '(same as org)';
              return `Delta: ${deltaStr} ${direction}`;
            }
            return '';
          },
        },
      },
      datalabels: {
        display: true,
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 11,
            family: 'Poppins, sans-serif',
          },
          color: "#4b5563",
          maxRotation: 45,
          minRotation: 45,
        },
        grid: {
          display: false,
        },
      },
      // Primary (left) Y axis for scores
      y: {
        min: 0,
        max: yAxisMax,
        ticks: {
          font: {
            size: 12,
            family: 'Poppins, sans-serif',
          },
          color: "#4b5563",
          stepSize: 1,
        },
        grid: {
          color: "#e5e7eb",
        },
      },

    },
    layout: {
      padding: {
        top: 25, // Add padding at the top to accommodate delta labels
      },
    },
  }), [deltas]);

  return (
    <Card className="rounded-2xl bg-white border border-gray-100 shadow-md" style={{ height: '100%' }}>
      <CardHeader>
        <div style={{ display: 'flex', flex: 1, alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <CardTitle className="text-title-section">{chartTitle}</CardTitle>
            <CardDescription className="text-description">{effectiveDescription}</CardDescription>
          </div>

        </div>
      </CardHeader>
      <CardContent className="space-relaxed h-full flex flex-col">
        {(!Array.isArray(pillars) || pillars.length === 0) ? (
          <div data-testid="scores-chart-placeholder" className="text-center py-8 text-gray-500">
            No pillar data available to populate this chart. When team pillar scores are available this chart will show Team vs Organization comparisons.
          </div>
        ) : (
          <div style={{
            flex: 1,
            height: chartHeight,
            minHeight: chartHeight ?? 288,
            maxHeight: chartHeight ?? 500,
          }}>
            <Bar
              key={chartKey}
              data={chartData}
              options={options}
              plugins={[ChartDataLabels]}
              redraw={true}
            />
          </div>
        )}

        {/* Delta Display */}
        {pillars.length > 0 && (
          <div className="border-t border-gray-200 pt-4 px-4 pb-4 mt-auto">
            <div className="narrative-title">{(teamName || 'Team') + ' Compared to Organization'}</div>
            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'minmax(48px, auto)' }}>
              {/* Always render as two rows of three chips — show up to 6 items, and a "+N more" chip if necessary */}
              {(() => {
                const maxChips = 6;
                const display = sortedPillars.slice(0, maxChips);
                const moreCount = Math.max(0, sortedPillars.length - maxChips);

                return (
                  <>
                    {display.map((pillar, index) => {
                      // If this is the last slot and there are more items, render a "more" chip
                      if (index === maxChips - 1 && moreCount > 0) {
                        return (
                          <div key="more" className={`bg-gray-50 border border-gray-300 rounded-lg p-2 text-center`}>
                            <div className="text-xs text-gray-600">+{moreCount} more</div>
                          </div>
                        );
                      }

                      const delta = deltas[index];
                      const deltaStr = delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
                      const deltaColor = delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-blue-600';
                      const bgColor = delta > 0 ? 'bg-green-50' : delta < 0 ? 'bg-red-50' : 'bg-gray-50';
                      const borderColor = delta > 0 ? 'border-green-300' : delta < 0 ? 'border-red-300' : 'border-blue-300';

                      return (
                        <div
                          key={pillar.key}
                          className={`${bgColor} ${borderColor} border rounded-lg p-2 text-center`}
                        >
                          <div className="text-xs text-gray-600 truncate" title={pillar.name}>
                            {pillar.name}
                          </div>
                          <div className={`text-sm font-bold ${deltaColor}`}>
                            {deltaStr}
                          </div>
                        </div>
                      );
                    })}
                    {/* Fill remaining slots with invisible placeholders so layout stays as 2x3 */}
                    {Array.from({ length: Math.max(0, 6 - display.length) }).map((_, i) => (
                      <div key={`placeholder-${i}`} className="invisible border rounded-lg p-2">placeholder</div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};